/**
 * Mutation hook for 保険請求統計一覧 modal picker submit.
 * Multi-massager → 1 PDF with N pages → open in new tab.
 */

import { useMutation } from '@tanstack/react-query';
import { useTheme } from '@shared/providers/theme/ThemeContext';
import { useClinicContext } from '@/hooks/main/useClinicContext';
import useAppToast from '@/hooks/useAppToast';
import { SERVICE_TYPE_A2, SERVICE_TYPE_R2 } from '@/constants/serviceType';
import { createStatisticalListPdfPreviewUrl } from '@/services/main/statisticalReport';
import { getApiErrorMessage } from '@/utils/apiError';

function resolveServiceType(theme: { serviceType?: string } | undefined): string {
  if (theme?.serviceType === SERVICE_TYPE_A2 || theme?.serviceType === SERVICE_TYPE_R2) {
    return theme.serviceType;
  }
  return SERVICE_TYPE_A2;
}

interface PrintListPayload {
  yearmonth: string;
  massagerIds: (number | 'all')[];
  outputOption: boolean;
  outputOption2: boolean;
}

export function useStatisticalPrintListMutation() {
  const { tenant, clinicNumber } = useClinicContext();
  const theme = useTheme();
  const toast = useAppToast();

  const { mutateAsync, isPending: isLoading } = useMutation<string, Error, PrintListPayload>({
    mutationFn: ({ yearmonth, massagerIds, outputOption, outputOption2 }) =>
      createStatisticalListPdfPreviewUrl(
        tenant,
        clinicNumber,
        yearmonth,
        massagerIds,
        outputOption,
        outputOption2,
        resolveServiceType(theme),
      ),
    onError: error => {
      toast.error(getApiErrorMessage(error, '印刷に失敗しました。'));
    },
  });

  const triggerPrint = async (payload: PrintListPayload) => {
    const tab = window.open('', '_blank');
    try {
      const url = await mutateAsync(payload);
      if (tab) {
        tab.location.href = url;
      } else {
        window.open(url, '_blank');
      }
    } catch {
      tab?.close();
    }
  };

  return { triggerPrint, isLoading };
}
